/**
 * Thumbnail scroll navigation component
 * Provides arrow-based scrolling for product image thumbnails
 */

/**
 * Minimum viewport width at which the vertical thumbnail column exists.
 * Mirrors the `min-width: 768px` guard in thumbnail-scroll-styles.css — below
 * it the thumbnails are laid out by different rules and must not be height-capped.
 */
const THUMBNAIL_COLUMN_MIN_WIDTH = 768;

/**
 * Tracks the image container so the thumbnail column height stays in sync as
 * the image settles (lazy-load, slick init, color swap) rather than being
 * measured once on a timer. Module-scoped so re-initialization can disconnect
 * the previous observer instead of stacking them.
 */
let imageHeightObserver: ResizeObserver | undefined;

/**
 * Initialize thumbnail scrolling functionality
 * Sets up arrow buttons and scroll behavior for vertical thumbnail navigation
 */
export function initThumbnailScrolling(): void {
  const $thumbnails = $(".product-detail__thumbnails");
  const $scrollWrapper = $(".product-detail__thumbnails-scroll-wrapper");
  const $scrollUp = $(".thumbnail-scroll-up");
  const $scrollDown = $(".thumbnail-scroll-down");

  if ($thumbnails.length === 0 || $scrollWrapper.length === 0) {
    return;
  }

  // Unbind any existing event handlers to prevent duplicates
  $scrollUp.off("click");
  $scrollDown.off("click");
  $scrollWrapper.off("scroll");
  $(window).off("resize.thumbnailScroll");
  imageHeightObserver?.disconnect();
  imageHeightObserver = undefined;

  /**
   * Size the thumbnail column to exactly match the main image, so the two
   * columns are flush top and bottom.
   *
   * Sets `height`, not just `max-height`: the inner scroll wrapper is
   * `height: 100%`, and a percentage height does not resolve against a parent
   * whose height is `auto` with only a `max-height` cap. Without a definite
   * height the wrapper grew to fit all its thumbnails, so it never overflowed —
   * meaning `scrollTop` could not move and any thumbnail past the column's
   * visible area was unreachable. The column is `box-sizing: border-box`, so
   * this value includes the 40px arrow gutters at each end.
   *
   * Only applies to the desktop `thumbnails-left` layout; the `thumbnails-below`
   * template lays the strip out horizontally and must keep its natural height.
   */
  function setThumbnailHeight(): void {
    const isVerticalColumn =
      window.innerWidth >= THUMBNAIL_COLUMN_MIN_WIDTH &&
      $(".product-layout-grid__images.layout--thumbnails-left").length > 0;

    if (!isVerticalColumn) {
      $thumbnails.css({ height: "", "max-height": "" });
      return;
    }

    const imageHeight =
      $(".product-detail__images-container").outerHeight() ?? 0;

    // A zero/absent measurement means the image hasn't laid out yet (mid-swap,
    // still lazy-loading). Keep the last good height — the observer fires again
    // once the image settles.
    if (imageHeight <= 0) {
      return;
    }

    $thumbnails.css({
      height: `${imageHeight}px`,
      "max-height": `${imageHeight}px`,
    });
  }

  function updateArrowStates(): void {
    const scrollTop = $scrollWrapper.scrollTop() ?? 0;
    const scrollHeight = ($scrollWrapper[0] as HTMLElement).scrollHeight;
    const containerHeight = $scrollWrapper.outerHeight() ?? 0;

    if (scrollTop <= 0) {
      $scrollUp.addClass("disabled");
    } else {
      $scrollUp.removeClass("disabled");
    }

    if (scrollTop + containerHeight >= scrollHeight - 1) {
      $scrollDown.addClass("disabled");
    } else {
      $scrollDown.removeClass("disabled");
    }
  }

  function scrollThumbnails(direction: "up" | "down"): void {
    const $thumbnail = $(".product-detail__thumbnail").first();
    const thumbnailHeight = $thumbnail.outerHeight(true) ?? 0;
    const currentScroll = $scrollWrapper.scrollTop() ?? 0;
    const targetScroll =
      direction === "up"
        ? Math.max(0, currentScroll - thumbnailHeight)
        : currentScroll + thumbnailHeight;

    $scrollWrapper.animate(
      {
        scrollTop: targetScroll,
      },
      300,
      function () {
        updateArrowStates();
      },
    );
  }

  setThumbnailHeight();
  setTimeout(updateArrowStates, 100);

  // Keep the column height pinned to the image as it settles. The image height
  // is not final when this runs: the swapped-in image is still lazy-loading and
  // slick has not yet initialized. A one-shot measurement caught the container
  // mid-layout and locked in a short column — the "thumbnails are shorter
  // sometimes" symptom.
  const imageContainer = document.querySelector<HTMLElement>(
    ".product-detail__images-container",
  );
  if (imageContainer && typeof ResizeObserver === "function") {
    imageHeightObserver = new ResizeObserver(function (): void {
      setThumbnailHeight();
      updateArrowStates();
    });
    imageHeightObserver.observe(imageContainer);
  }

  $(window).on("resize.thumbnailScroll", function () {
    setThumbnailHeight();
    updateArrowStates();
  });

  $scrollUp.on("click", function (e: JQuery.ClickEvent) {
    e.preventDefault();
    if (!$(this).hasClass("disabled")) {
      scrollThumbnails("up");
    }
  });

  $scrollDown.on("click", function (e: JQuery.ClickEvent) {
    e.preventDefault();
    if (!$(this).hasClass("disabled")) {
      scrollThumbnails("down");
    }
  });

  $scrollWrapper.on("scroll", function () {
    updateArrowStates();
  });
}

/**
 * Reinitialize thumbnail scrolling after product images change
 * Call this after swatch clicks or dynamic image updates
 */
export function reinitThumbnailScrolling(): void {
  setTimeout(() => {
    initThumbnailScrolling();
  }, 500);
}
